import { create } from "zustand";

// =============================================================================
// Types
// =============================================================================

type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
	id: string;
	type: ToastType;
	title: string;
	description?: string;
}

interface ModalState {
	isOpen: boolean;
	data?: unknown;
}

interface UIState {
	// Sidebar
	sidebarOpen: boolean;
	sidebarCollapsed: boolean;

	// Modals (keyed by modal name)
	modals: Record<string, ModalState>;

	// Toasts
	toasts: Toast[];

	// Loading overlay
	isLoading: boolean;
	loadingMessage?: string | undefined;
}

interface UIActions {
	// Sidebar
	toggleSidebar: () => void;
	setSidebarOpen: (open: boolean) => void;
	toggleSidebarCollapsed: () => void;
	setSidebarCollapsed: (collapsed: boolean) => void;

	// Modals
	openModal: <T = unknown>(name: string, data?: T) => void;
	closeModal: (name: string) => void;
	getModalData: <T = unknown>(name: string) => T | undefined;

	// Toasts
	addToast: (toast: Omit<Toast, "id">) => void;
	removeToast: (id: string) => void;
	clearToasts: () => void;

	// Loading
	setLoading: (loading: boolean, message?: string) => void;
}

type UIStore = UIState & UIActions;

// =============================================================================
// Helpers
// =============================================================================

const generateId = () => Math.random().toString(36).substring(2, 9);

// =============================================================================
// Initial State
// =============================================================================

const initialState: UIState = {
	sidebarOpen: true,
	sidebarCollapsed: false,
	modals: {},
	toasts: [],
	isLoading: false,
	loadingMessage: undefined,
};

// =============================================================================
// Store
// =============================================================================

export const useUIStore = create<UIStore>()((set, get) => ({
	...initialState,

	// Sidebar actions
	toggleSidebar: () => {
		set((state) => ({ sidebarOpen: !state.sidebarOpen }));
	},

	setSidebarOpen: (open) => {
		set({ sidebarOpen: open });
	},

	toggleSidebarCollapsed: () => {
		set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
	},

	setSidebarCollapsed: (collapsed) => {
		set({ sidebarCollapsed: collapsed });
	},

	// Modal actions
	openModal: (name, data) => {
		set((state) => ({
			modals: {
				...state.modals,
				[name]: { isOpen: true, data },
			},
		}));
	},

	closeModal: (name) => {
		set((state) => ({
			modals: {
				...state.modals,
				[name]: { isOpen: false, data: undefined },
			},
		}));
	},

	getModalData: <T = unknown>(name: string): T | undefined => {
		const { modals } = get();
		return modals[name]?.data as T | undefined;
	},

	// Toast actions
	addToast: (toast) => {
		const id = generateId();
		set((state) => ({
			toasts: [...state.toasts, { ...toast, id }],
		}));

		// Auto-remove toast after 5 seconds
		setTimeout(() => {
			get().removeToast(id);
		}, 5000);
	},

	removeToast: (id) => {
		set((state) => ({
			toasts: state.toasts.filter((t) => t.id !== id),
		}));
	},

	clearToasts: () => {
		set({ toasts: [] });
	},

	// Loading actions
	setLoading: (loading, message) => {
		set({ isLoading: loading, loadingMessage: message });
	},
}));

// =============================================================================
// Selectors (for convenience)
// =============================================================================

export const useModal = (name: string) => {
	const isOpen = useUIStore((state) => state.modals[name]?.isOpen ?? false);
	const data = useUIStore((state) => state.modals[name]?.data);
	const openModal = useUIStore((state) => state.openModal);
	const closeModal = useUIStore((state) => state.closeModal);

	return {
		isOpen,
		data,
		open: <T = unknown>(modalData?: T) => openModal(name, modalData),
		close: () => closeModal(name),
	};
};
